import { useCallback } from 'react';
import {
  type WeeklySchedule,
  type Weekday,
  type ScheduleTimeSlot,
} from '@/features/plants/schemas/schedule-plant.schema.ts';
import {
  sortTimeSlotsByStartTime,
  convertTimeToMinutes,
  convertMinutesToTimeString,
} from '@/features/plants/components/plant-schedule/schedule/utils/time.ts';
import {
  SCHEDULE_HOURS_IN_DAY,
  SCHEDULE_MINUTES_IN_HOUR,
  SCHEDULE_SNAP_MINUTES,
} from '@/features/plants/components/plant-schedule/schedule/constants.ts';

export type TimeSlotValidationReason =
  | 'start-time-after-end-time'
  | 'start-time-overlap'
  | 'end-time-overlap'
  | 'complete-overlap'
  | 'partial-overlap';

export type ConflictingSlotInfo = {
  slot: ScheduleTimeSlot;
  reason: TimeSlotValidationReason;
};

export type TimeSlotValidationResult = {
  isValid: boolean;
  error?: string;
  reason?: TimeSlotValidationReason;
  conflictingSlots?: ConflictingSlotInfo[];
};

export function useWeeklyScheduleActions(
  weeklySchedule: WeeklySchedule,
  onChangeWeeklySchedule: (weeklySchedule: WeeklySchedule) => void
) {
  const checkTimeSlotOverlap = useCallback(
    (
      day: Weekday | null,
      startTime: string,
      endTime: string,
      excludeSlotId?: string
    ): TimeSlotValidationResult => {
      if (!day) return { isValid: true };

      const startMinutes = convertTimeToMinutes(startTime);
      const endMinutes = convertTimeToMinutes(endTime);

      if (startMinutes >= endMinutes) {
        return {
          isValid: false,
          error: 'Start time must be before end time',
          reason: 'start-time-after-end-time',
        };
      }

      const existingTimeSlots = weeklySchedule[day] || [];
      const conflictingSlots: ConflictingSlotInfo[] = [];

      for (const timeSlot of existingTimeSlots) {
        if ((excludeSlotId && timeSlot && timeSlot.id === excludeSlotId) || !timeSlot)
          continue;

        const existingStartMinutes = convertTimeToMinutes(timeSlot.startTime);
        const existingEndMinutes = convertTimeToMinutes(timeSlot.endTime);

        if (startMinutes < existingEndMinutes && endMinutes > existingStartMinutes) {
          let individualReason: TimeSlotValidationReason;

          if (startMinutes >= existingStartMinutes && endMinutes <= existingEndMinutes) {
            individualReason = 'complete-overlap';
          } else if (
            startMinutes <= existingStartMinutes &&
            endMinutes >= existingEndMinutes
          ) {
            individualReason = 'complete-overlap';
          } else if (
            startMinutes >= existingStartMinutes &&
            startMinutes < existingEndMinutes
          ) {
            individualReason = 'start-time-overlap';
          } else if (endMinutes > existingStartMinutes && endMinutes <= existingEndMinutes) {
            individualReason = 'end-time-overlap';
          } else {
            individualReason = 'partial-overlap';
          }

          conflictingSlots.push({
            slot: timeSlot,
            reason: individualReason,
          });
        }
      }

      if (conflictingSlots.length === 0) {
        return { isValid: true };
      }

      let overallReason: TimeSlotValidationReason;
      const hasStartOverlap = conflictingSlots.some((c) => c.reason === 'start-time-overlap');
      const hasEndOverlap = conflictingSlots.some((c) => c.reason === 'end-time-overlap');
      const hasCompleteOverlap = conflictingSlots.some((c) => c.reason === 'complete-overlap');

      if (hasCompleteOverlap) {
        overallReason = 'complete-overlap';
      } else if (hasStartOverlap && hasEndOverlap) {
        overallReason = 'complete-overlap';
      } else if (hasStartOverlap) {
        overallReason = 'start-time-overlap';
      } else if (hasEndOverlap) {
        overallReason = 'end-time-overlap';
      } else {
        overallReason = 'partial-overlap';
      }

      const slotsList = conflictingSlots
        .map((c) => `${c.slot.startTime} - ${c.slot.endTime}`)
        .join(', ');
      const errorMessage =
        conflictingSlots.length === 1
          ? `Time slot overlaps with existing slot (${slotsList})`
          : `Time slot overlaps with ${conflictingSlots.length} existing slots (${slotsList})`;

      return {
        isValid: false,
        error: errorMessage,
        reason: overallReason,
        conflictingSlots,
      };
    },
    [weeklySchedule]
  );

  const updateTimeSlotTimes = useCallback(
    (day: Weekday, slotId: string, startTime: string, endTime: string) => {
      const updatedSlots =
        weeklySchedule[day]?.map((timeSlot) =>
          timeSlot.id === slotId ? { ...timeSlot, startTime, endTime } : timeSlot
        ) || [];

      onChangeWeeklySchedule({
        ...weeklySchedule,
        [day]: sortTimeSlotsByStartTime(updatedSlots),
      });
    },
    [weeklySchedule, onChangeWeeklySchedule]
  );

  const saveTimeSlot = useCallback(
    (day: Weekday, timeSlot: ScheduleTimeSlot) => {
      const existingSlots = weeklySchedule[day] || [];
      const slotExists = existingSlots.some((s) => s.id === timeSlot.id);
      const updatedSlots = slotExists
        ? existingSlots.map((s) => (s.id === timeSlot.id ? timeSlot : s))
        : [...existingSlots, timeSlot];

      onChangeWeeklySchedule({
        ...weeklySchedule,
        [day]: sortTimeSlotsByStartTime(updatedSlots),
      });
    },
    [weeklySchedule, onChangeWeeklySchedule]
  );

  const addTimeSlot = useCallback(
    (day: Weekday, timeSlot: ScheduleTimeSlot) => {
      const existingSlots = weeklySchedule[day] || [];
      const updatedSlots = [...existingSlots, timeSlot];
      onChangeWeeklySchedule({
        ...weeklySchedule,
        [day]: sortTimeSlotsByStartTime(updatedSlots),
      });
    },
    [weeklySchedule, onChangeWeeklySchedule]
  );

  const deleteTimeSlot = useCallback(
    (day: Weekday, slotId: string) => {
      const updatedSlots = weeklySchedule[day]?.filter((s) => s.id !== slotId) || [];
      onChangeWeeklySchedule({
        ...weeklySchedule,
        [day]: updatedSlots,
      });
    },
    [weeklySchedule, onChangeWeeklySchedule]
  );

  const findAvailableTimeWindow = useCallback(
    (
      day: Weekday,
      clickPositionMinutes: number,
      requestedDurationMinutes: number
    ): { startMinutes: number; endMinutes: number } => {
      const existingSlots = sortTimeSlotsByStartTime(weeklySchedule[day] || []);

      let earliestAvailableMinute = 0;
      let latestAvailableMinute = SCHEDULE_HOURS_IN_DAY * SCHEDULE_MINUTES_IN_HOUR;

      for (const slot of existingSlots) {
        const slotStartMinutes = convertTimeToMinutes(slot.startTime);
        const slotEndMinutes = convertTimeToMinutes(slot.endTime);
        if (slotEndMinutes <= clickPositionMinutes) {
          earliestAvailableMinute = Math.max(earliestAvailableMinute, slotEndMinutes);
        }
        if (slotStartMinutes >= clickPositionMinutes) {
          latestAvailableMinute = Math.min(latestAvailableMinute, slotStartMinutes);
          break;
        }
      }

      const maxAvailableSpace = Math.max(0, latestAvailableMinute - earliestAvailableMinute);
      const actualDuration = Math.min(requestedDurationMinutes, maxAvailableSpace);

      let proposedStartMinutes = Math.max(
        earliestAvailableMinute,
        clickPositionMinutes - Math.floor(actualDuration / 2)
      );
      let proposedEndMinutes = proposedStartMinutes + actualDuration;
      if (proposedEndMinutes > latestAvailableMinute) {
        proposedEndMinutes = latestAvailableMinute;
        proposedStartMinutes = latestAvailableMinute - actualDuration;
      }

      const snapToGrid = (minutes: number) =>
        Math.round(minutes / SCHEDULE_SNAP_MINUTES) * SCHEDULE_SNAP_MINUTES;
      proposedStartMinutes = Math.max(
        earliestAvailableMinute,
        Math.min(latestAvailableMinute, snapToGrid(proposedStartMinutes))
      );
      proposedEndMinutes = Math.max(
        earliestAvailableMinute,
        Math.min(latestAvailableMinute, snapToGrid(proposedEndMinutes))
      );
      if (proposedEndMinutes <= proposedStartMinutes) {
        proposedEndMinutes = Math.min(latestAvailableMinute, proposedStartMinutes + 5);
      }

      return {
        startMinutes: proposedStartMinutes,
        endMinutes: proposedEndMinutes,
      };
    },
    [weeklySchedule]
  );

  return {
    checkTimeSlotOverlap,
    updateTimeSlotTimes,
    saveTimeSlot,
    addTimeSlot,
    deleteTimeSlot,
    findAvailableTimeWindow,
    convertTimeToMinutes,
    convertMinutesToTimeString,
    sortTimeSlotsByStartTime,
  };
}
